(() => {
  'use strict';

  const instances = new WeakMap();
  const nonBlank = (value) => typeof value === 'string' && value.trim() !== '';
  const ctaIsComplete = (label, url) => !nonBlank(label) || nonBlank(url);
  const canRotate = ({ slots, autoplay, rotationStopped, hovered, designMode, reducedMotion, visible }) =>
    slots > 1 && autoplay && !rotationStopped && !hovered && !designMode && !reducedMotion && visible;
  const independentHeadingFor = (slots, slotId, fallback) => {
    const slot = slots.find((candidate) => candidate.block.slotId === slotId);
    return { slotId, text: nonBlank(slot?.content?.title) ? slot.content.title : fallback, slot };
  };
  const isIndependentHeadingSlot = (active, slot, independentHeading) =>
    Boolean(independentHeading && active && slot.block.slotId === independentHeading.slotId);
  const syncRotationControlLabel = (control, rotationStopped) => {
    if (!control) return;
    const label = rotationStopped ? 'Start slide rotation' : 'Pause slide rotation';
    control.textContent = label;
    control.setAttribute('aria-label', label);
  };

  function payloadFor(root) {
    const blockPayloads = root.querySelectorAll('[data-theme-home-hero-block-payload]');
    if (!blockPayloads.length) return null;
    try {
      return {
        heading: root.dataset.themeHomeHeroHeading || root.querySelector('.theme-home-hero__headline')?.textContent?.trim() || 'Featured promotions',
        settings: {
          titleMode: root.dataset.themeHomeHeroTitleMode,
          independentH1Slide: root.dataset.themeHomeHeroIndependentH1Slide || 'slot-1',
          frameSource: root.dataset.themeHomeHeroFrameSource
        },
        blocks: Array.from(blockPayloads, (node) => JSON.parse(node.textContent))
      };
    } catch (error) {
      console.warn('[theme-home-hero] Payload could not be read.', error);
      return null;
    }
  }

  function resolveContent(content) {
    if (!content || typeof content !== 'object') return { content, valid: false, error: 'Slide content is missing.' };
    if (!content.mobile && !content.desktop) return { content, valid: false, error: 'Choose a desktop or mobile image.' };
    if (!ctaIsComplete(content.primaryLabel, content.primaryUrl) || !ctaIsComplete(content.secondaryLabel, content.secondaryUrl)) {
      return { content, valid: false, error: 'Each CTA needs both a label and a link.' };
    }
    return { content, valid: true };
  }

  function resolveSlots(payload) {
    const slots = [];
    const errors = [];
    const diagnostics = [];
    const slotCounts = new Map();
    const keyCounts = new Map();

    payload.blocks.forEach((block) => {
      slotCounts.set(block.slotId, (slotCounts.get(block.slotId) || 0) + 1);
      if (nonBlank(block.analyticsKey)) keyCounts.set(block.analyticsKey, (keyCounts.get(block.analyticsKey) || 0) + 1);
    });

    payload.blocks.forEach((block) => {
      if (!nonBlank(block.slotId) || slotCounts.get(block.slotId) !== 1) {
        const error = 'Slot ' + (block.slotId || '(blank)') + ' is duplicated or missing and is excluded.';
        errors.push(error);
        diagnostics.push({ block, selected: null, errors: [error] });
        return;
      }
      if (!block.enabled) {
        diagnostics.push({ block, selected: null, errors: ['This slide is disabled.'] });
        return;
      }
      const resolved = resolveContent(block.content);
      const slotErrors = resolved.valid ? [] : ['Slot ' + block.slotId + ': ' + resolved.error];
      const duplicateKey = nonBlank(block.analyticsKey) && keyCounts.get(block.analyticsKey) > 1;
      if (duplicateKey) slotErrors.push('Merchant analytics key ' + block.analyticsKey + ' is duplicated and suppressed from events.');
      errors.push(...slotErrors);
      diagnostics.push({ block, selected: resolved.valid ? resolved.content : null, errors: slotErrors });
      if (!resolved.valid) return;
      slots.push({ block, content: resolved.content, analyticsKey: duplicateKey ? '' : block.analyticsKey });
    });

    return { slots, errors, diagnostics };
  }

  function buildMedia(slot, eager, fallbackAlt) {
    const picture = document.createElement('picture');
    picture.className = 'theme-home-hero__slide-media';
    if (slot.content.desktop && slot.content.desktop.srcset) {
      const source = document.createElement('source');
      source.media = '(min-width: 48rem)';
      source.sizes = '100vw';
      source.srcset = slot.content.desktop.srcset;
      picture.append(source);
    }
    const media = slot.content.mobile || slot.content.desktop;
    const image = document.createElement('img');
    image.className = 'theme-home-hero__image';
    image.src = media && media.url ? media.url : '';
    image.alt = slot.content.title || fallbackAlt || '';
    image.loading = eager ? 'eager' : 'lazy';
    image.fetchPriority = eager ? 'high' : 'low';
    image.decoding = 'async';
    image.sizes = '100vw';
    image.style.objectFit = slot.content.fit || 'contain';
    image.style.setProperty('--theme-home-hero-mobile-position', slot.content.mobilePosition || 'center center');
    image.style.setProperty('--theme-home-hero-desktop-position', slot.content.desktopPosition || 'center center');
    picture.append(image);
    return picture;
  }

  function appendCta(actions, kind, label, url, position, slot) {
    if (!nonBlank(label) || !nonBlank(url)) return;
    const link = document.createElement('a');
    link.className = 'theme-home-hero__cta theme-home-hero__cta--' + kind;
    link.href = url;
    link.textContent = label;
    const attributes = {
      'data-theme-event': 'select_home_module', 'data-theme-event-schema-version': 'bweb014_v1',
      'data-theme-event-module-id': 'HZ-01', 'data-theme-event-module-type': 'hero_launch_rail',
      'data-theme-event-cta-label': label, 'data-theme-event-cta-target': url,
      'data-theme-event-template-name': 'home', 'data-theme-event-template-type': 'core_commerce',
      'data-theme-event-device-category': 'other', 'data-theme-event-funnel-stage': 'discovery',
      'data-theme-event-page-path': location.pathname, 'data-theme-event-session-id': 'pending_session_id',
      'data-theme-event-position-index': String(position),
      'data-theme-home-hero-block-id': slot.block.id,
      'data-theme-event-hero-block-id': slot.block.id
    };
    if (nonBlank(slot.analyticsKey)) attributes['data-theme-event-merchant-analytics-key'] = slot.analyticsKey;
    Object.entries(attributes).forEach(([name, value]) => link.setAttribute(name, value));
    actions.append(link);
  }

  class HomeHero {
    constructor(root, payload) {
      this.root = root;
      this.payload = payload;
      this.media = root.querySelector('[data-theme-home-hero-media]');
      this.controls = root.querySelector('[data-theme-home-hero-controls]');
      this.status = root.querySelector('[data-theme-home-hero-status]');
      this.independentHeading = root.querySelector('[data-theme-home-hero-independent-heading]');
      this.designMode = root.dataset.themeHomeHeroDesignMode === 'true';
      this.autoplay = root.dataset.themeHomeHeroAutoplay === 'true';
      this.interval = Math.max(5, Number(root.dataset.themeHomeHeroInterval || 7)) * 1000;
      this.current = 0;
      this.rotationStopped = false;
      this.hovered = false;
      this.rotationTimer = null;
      this.pointerStart = null;
      this.suppressSlideClickUntil = 0;
      this.onVisibility = this.onVisibility.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onPointerCancel = this.onPointerCancel.bind(this);
      this.onClick = this.onClick.bind(this);
      document.addEventListener('visibilitychange', this.onVisibility);
      addEventListener('pageshow', this.onPageShow);
      root.addEventListener('pointerdown', this.onPointerDown);
      root.addEventListener('pointerup', this.onPointerUp);
      root.addEventListener('pointercancel', this.onPointerCancel);
      root.addEventListener('click', this.onClick, true);
      root.addEventListener('pointerenter', () => { this.hovered = true; this.armRotation(); });
      root.addEventListener('pointerleave', () => { this.hovered = false; this.armRotation(); });
      root.addEventListener('focusin', () => {
        this.rotationStopped = true;
        this.syncRotationControl();
        this.armRotation();
      });
      this.reconcile();
    }

    destroy() {
      clearTimeout(this.rotationTimer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      removeEventListener('pageshow', this.onPageShow);
      this.root.removeEventListener('pointerdown', this.onPointerDown);
      this.root.removeEventListener('pointerup', this.onPointerUp);
      this.root.removeEventListener('pointercancel', this.onPointerCancel);
      this.root.removeEventListener('click', this.onClick, true);
      instances.delete(this.root);
    }

    reconcile(options = {}) {
      const result = resolveSlots(this.payload);
      const prior = this.slots && this.slots[this.current];
      this.slots = result.slots;
      if (options.index !== undefined) this.current = options.index;
      if (prior && options.index === undefined) {
        const preserved = this.slots.findIndex((slot) => slot.block.id === prior.block.id);
        if (preserved >= 0) this.current = preserved;
      }
      if (this.current >= this.slots.length) this.current = Math.max(0, this.slots.length - 1);
      this.lastResolution = result;
      this.render();
      this.armRotation();
      if (options.announce && this.slots[this.current]) this.status.textContent = 'Slide ' + (this.current + 1) + ' of ' + this.slots.length + ': ' + (this.slots[this.current].content.title || 'Featured promotion');
    }

    render() {
      this.applyFrameRatios();
      const independentHeading = this.prepareIndependentHeading();
      this.media.querySelectorAll('.theme-home-hero__slide').forEach((node) => node.remove());
      this.media.querySelectorAll('[data-theme-home-hero-server-fallback]').forEach((node) => node.remove());
      this.controls.replaceChildren();
      if (!this.slots.length) {
        this.controls.hidden = true;
        return;
      }
      this.slots.forEach((slot, index) => {
        const active = index === this.current;
        const panel = document.createElement('div');
        panel.className = 'theme-home-hero__slide';
        panel.dataset.themeHomeHeroBlockId = slot.block.id;
        panel.dataset.themeHomeHeroSlotId = slot.block.slotId;
        panel.setAttribute('role', 'group');
        panel.setAttribute('aria-roledescription', 'slide');
        panel.setAttribute('aria-label', (index + 1) + ' of ' + this.slots.length);
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
        panel.inert = !active;
        panel.append(buildMedia(slot, index === 0, this.payload.heading));
        if (nonBlank(slot.content.slideUrl)) {
          const link = document.createElement('a');
          link.className = 'theme-home-hero__slide-link';
          link.href = slot.content.slideUrl;
          link.setAttribute('aria-label', slot.content.title || this.payload.heading || 'Featured promotion');
          panel.append(link);
        }
        const content = document.createElement('div');
        content.className = 'theme-home-hero__content';
        const titleTypography = [
          ['titleFontFamily', '--theme-home-hero-heading-font-family'],
          ['titleFontSize', '--theme-home-hero-heading-font-size'],
          ['titleFontWeight', '--theme-home-hero-heading-font-weight'],
          ['titleLineHeight', '--theme-home-hero-heading-line-height'],
          ['titleLetterSpacing', '--theme-home-hero-heading-letter-spacing']
        ];
        titleTypography.forEach(([key, property]) => {
          if (slot.content[key] && slot.content[key] !== 'inherit') content.style.setProperty(property, slot.content[key]);
        });
        if (nonBlank(slot.content.eyebrow)) {
          const eyebrow = document.createElement('p');
          eyebrow.className = 'theme-home-hero__eyebrow';
          eyebrow.textContent = slot.content.eyebrow;
          content.append(eyebrow);
        }
        const useIndependentHeading = isIndependentHeadingSlot(active, slot, independentHeading);
        if (useIndependentHeading) {
          independentHeading.node.className = 'theme-home-hero__slide-title';
          content.append(independentHeading.node);
        } else if (this.payload.settings.titleMode === 'independent' && nonBlank(slot.content.title)) {
          const title = document.createElement('p');
          title.className = 'theme-home-hero__slide-title';
          title.textContent = slot.content.title;
          content.append(title);
        }
        if (nonBlank(slot.content.subcopy)) {
          const subcopy = document.createElement('p');
          subcopy.className = 'theme-home-hero__subcopy';
          subcopy.textContent = slot.content.subcopy;
          content.append(subcopy);
        }
        const actions = document.createElement('div');
        actions.className = 'theme-home-hero__actions';
        appendCta(actions, 'primary', slot.content.primaryLabel, slot.content.primaryUrl, index + 1, slot);
        appendCta(actions, 'secondary', slot.content.secondaryLabel, slot.content.secondaryUrl, index + 1, slot);
        if (actions.children.length) content.append(actions);
        panel.append(content);
        this.media.append(panel);
      });
      this.renderControls();
    }

    applyFrameRatios() {
      if (!this.slots.length) return;
      const first = this.slots[0].content;
      if (this.payload.settings.frameSource === 'first_enabled_slide') {
        const mobile = first.mobile || first.desktop;
        const desktop = first.desktop || first.mobile;
        if (mobile && mobile.ratio) this.root.style.setProperty('--theme-home-hero-mobile-ratio', mobile.ratio);
        if (desktop && desktop.ratio) this.root.style.setProperty('--theme-home-hero-desktop-ratio', desktop.ratio);
      }
    }

    prepareIndependentHeading() {
      if (this.payload.settings.titleMode !== 'independent' || !this.independentHeading) return null;
      this.media.prepend(this.independentHeading);
      const heading = independentHeadingFor(this.slots, this.payload.settings.independentH1Slide, this.payload.heading);
      this.independentHeading.textContent = heading.text;
      this.independentHeading.className = 'theme-home-hero__slide-title visually-hidden';
      this.independentHeading.hidden = false;
      this.independentHeading.removeAttribute('aria-hidden');
      return { ...heading, node: this.independentHeading };
    }

    renderControls() {
      if (this.slots.length < 2) {
        this.controls.hidden = true;
        return;
      }
      this.controls.hidden = false;
      this.controls.dataset.contrast = this.slots[this.current].block.navContrast || 'automatic';
      if (this.autoplay) {
        const pause = document.createElement('button');
        pause.type = 'button';
        pause.className = 'theme-home-hero__control';
        pause.dataset.themeHomeHeroRotationControl = '';
        pause.dataset.heroNavigation = 'rotation';
        syncRotationControlLabel(pause, this.rotationStopped);
        pause.addEventListener('click', () => {
          this.rotationStopped = !this.rotationStopped;
          this.syncRotationControl();
          this.armRotation();
        });
        this.controls.append(pause);
      }
      const previous = document.createElement('button');
      previous.type = 'button';
      previous.className = 'theme-home-hero__control theme-home-hero__control--previous';
      previous.dataset.heroNavigation = 'previous';
      previous.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="m15 4-8 8 8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      previous.setAttribute('aria-label', 'Previous slide');
      previous.addEventListener('click', () => this.go(this.current - 1, true));
      this.controls.append(previous);

      const dots = document.createElement('div');
      dots.className = 'theme-home-hero__dots';
      this.slots.forEach((slot, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'theme-home-hero__dot';
        dot.dataset.style = this.root.dataset.themeHomeHeroDotStyle || 'solid';
        dot.dataset.heroNavigation = 'dot-' + index;
        dot.dataset.themeHomeHeroSlotId = slot.block.slotId;
        dot.innerHTML = '<span aria-hidden="true" class="theme-home-hero__dot-mark"></span>';
        dot.setAttribute('aria-current', String(index === this.current));
        dot.setAttribute('aria-label', 'Show slide ' + (index + 1) + ' of ' + this.slots.length);
        dot.addEventListener('click', () => this.go(index, true));
        dots.append(dot);
      });
      this.controls.append(dots);

      const next = document.createElement('button');
      next.type = 'button';
      next.className = 'theme-home-hero__control theme-home-hero__control--next';
      next.dataset.heroNavigation = 'next';
      next.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="m9 4 8 8-8 8" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      next.setAttribute('aria-label', 'Next slide');
      next.addEventListener('click', () => this.go(this.current + 1, true));
      this.controls.append(next);
    }

    go(index, announce) {
      const focusedNavigation = this.controls.contains(document.activeElement) ? document.activeElement.dataset.heroNavigation : null;
      this.current = (index + this.slots.length) % this.slots.length;
      this.reconcile({ index: this.current, announce });
      if (focusedNavigation) this.controls.querySelector(`[data-hero-navigation="${focusedNavigation}"]`)?.focus();
    }

    syncRotationControl() {
      syncRotationControlLabel(this.controls.querySelector('[data-theme-home-hero-rotation-control]'), this.rotationStopped);
    }

    armRotation() {
      clearTimeout(this.rotationTimer);
      const allowed = canRotate({
        slots: this.slots ? this.slots.length : 0,
        autoplay: this.autoplay,
        rotationStopped: this.rotationStopped,
        hovered: this.hovered,
        designMode: this.designMode,
        reducedMotion: matchMedia('(prefers-reduced-motion: reduce)').matches,
        visible: document.visibilityState === 'visible'
      });
      if (allowed) this.rotationTimer = setTimeout(() => this.go(this.current + 1, false), this.interval);
    }

    onVisibility() {
      this.armRotation();
    }

    onPageShow() {
      this.armRotation();
    }

    onPointerDown(event) {
      this.suppressSlideClickUntil = 0;
      if (event.pointerType !== 'mouse' && !event.target.closest?.('button, .theme-home-hero__cta')) this.pointerStart = { x: event.clientX, y: event.clientY };
    }

    onPointerUp(event) {
      if (!this.pointerStart) return;
      const start = this.pointerStart;
      this.pointerStart = null;
      const x = event.clientX - start.x;
      const y = event.clientY - start.y;
      if (Math.abs(x) >= 40 && Math.abs(x) > Math.abs(y)) {
        this.suppressSlideClickUntil = Date.now() + 500;
        this.go(this.current + (x < 0 ? 1 : -1), true);
      }
    }

    onClick(event) {
      if (event.detail > 0 && Date.now() < this.suppressSlideClickUntil && event.target.closest?.('.theme-home-hero__slide-link')) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    onPointerCancel() {
      this.pointerStart = null;
    }
  }

  function initialize(root) {
    if (instances.has(root)) return instances.get(root);
    const payload = payloadFor(root);
    if (!payload) return null;
    const instance = new HomeHero(root, payload);
    instances.set(root, instance);
    return instance;
  }

  function initializeAll(scope) {
    (scope || document).querySelectorAll('[data-theme-home-hero]').forEach(initialize);
  }

  if (globalThis.__themeHomeHeroTest) {
    globalThis.__themeHomeHeroTest = { resolveSlots, canRotate, independentHeadingFor, isIndependentHeadingSlot, syncRotationControlLabel, HomeHero };
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches && event.target.matches('[data-theme-home-hero]') ? event.target : event.target.querySelector('[data-theme-home-hero]');
    if (root && instances.has(root)) instances.get(root).destroy();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest && event.target.closest('[data-theme-home-hero]');
    const instance = root && instances.get(root);
    if (!instance) return;
    instance.rotationStopped = true;
    const index = instance.slots.findIndex((slot) => slot.block.id === event.target.dataset.themeHomeHeroBlockId);
    instance.reconcile(index >= 0 ? { index } : {});
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest && event.target.closest('[data-theme-home-hero]');
    const instance = root && instances.get(root);
    if (instance) instance.reconcile();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll());
  else initializeAll();
})();
